import React, { useMemo, useRef } from "react";
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import { useHistory } from "react-router-dom";
import { IconContext } from "react-icons";

import { useAuth } from "../../hooks/auth";
import { useCompanies } from "../../hooks/companies";
import useDashboardWeather from "../../hooks/useDashboardWeather";
import getGreetingByHour from "../../utils/getGreetingByHour";
import getDisplayName from "../../utils/getDisplayName";
import autopilotLogo from "../../assets/img/autopilot-logo.png";
import getWeatherIconByKey from "../../components/dashboard/weatherIconMapper";
import RadarLogistico from "../Empresa/Dashboard/componentsRadar/RadarLogistico";
import InsightsCard from "../../components/autopilot/InsightsCard";
import MarketIndicatorsCard from "../../components/autopilot/MarketIndicatorsCard";
import FreightIndicatorsCard from "../../components/autopilot/FreightIndicatorsCard";
import EmissoesCarbonoCard from "../../components/autopilot/EmissoesCarbonoCard";
import HealthGauge from "../../components/autopilot/HealthGauge";
import useAutopilotInsightsGeral from "../../hooks/useAutopilotInsightsGeral";
import getHeroTitle from "../../utils/getHeroTitle";
import { getAutopilotHealthSummary } from "../../components/autopilot/healthSummary";
import { QUICK_PROMPTS } from "../../components/autopilot/quickPrompts";

const heroCardStyle = {
  overflow: "hidden",
};

const weatherBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  color: "var(--everlog-card-muted)",
  fontSize: "0.78rem",
  background: "var(--everlog-card-surface-soft)",
  border: "1px solid var(--everlog-card-border)",
  borderRadius: 999,
  padding: "0.28rem 0.65rem",
  lineHeight: 1,
};

const heroBodyStyle = {
  padding: "1.6rem 1.6rem 1.35rem",
  background: "var(--everlog-card-hero-bg)",
};

const heroLeadStyle = {
  flex: "1 1 560px",
  minWidth: 0,
  maxWidth: 760,
  paddingTop: "0.4rem",
};

const heroIdentityRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.85rem",
  marginBottom: "0.75rem",
  flexWrap: "wrap",
};

const heroGreetingStyle = {
  color: "var(--theme-accent)",
  fontSize: "0.76rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const heroTitleStyle = {
  marginBottom: "0.45rem",
  fontSize: "1.35rem",
  lineHeight: 1.2,
  letterSpacing: "normal",
  color: "var(--everlog-card-title)",
  fontWeight: 700,
};

const launcherCardStyle = {
  marginTop: "1.15rem",
  borderRadius: "var(--everlog-card-radius)",
  border: "1px solid var(--theme-accent-border)",
  padding: "1rem",
};

const launcherButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "0.85rem",
  border: "1px solid var(--theme-accent-border)",
  borderRadius: 14,
  background: "var(--everlog-card-surface-soft)",
  color: "var(--everlog-card-title)",
  padding: "0.95rem 1rem",
  cursor: "pointer",
  textAlign: "left",
};

const promptGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "0.6rem",
  marginTop: "0.75rem",
};

const promptButtonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.65rem",
  borderRadius: 12,
  // mesma receita do launcher ("Clique aqui..."): borda accent sutil (uma cor
  // só, uniforme) + véu translúcido que clareia o que está atrás — contraste
  // relativo sobre o gradiente do hero (feedback Rodrigo 12/06)
  border: "1px solid var(--theme-accent-border)",
  background: "var(--everlog-card-surface-soft)",
  color: "var(--everlog-card-text)",
  textAlign: "left",
  padding: "0.82rem 0.9rem",
  cursor: "pointer",
};

const getCompanyId = (company) =>
  company?.value || company?.id || company?.companyId || company?.empresaId;

const normalizeCondition = (condition) => {
  if (typeof condition !== "string") return null;
  const cleaned = condition
    .replace(/\b(openweather|weatherapi|accuweather|climatempo)\b/gi, "")
    .replace(/[|()]/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  if (!cleaned) return null;
  return cleaned.length > 40 ? `${cleaned.slice(0, 37)}...` : cleaned;
};

const Autopilot = () => {
  const { user } = useAuth();
  const { empresa } = useCompanies();
  const history = useHistory();
  const monitorRef = useRef(null);

  const greeting = getGreetingByHour();
  const displayName = getDisplayName(user);
  const greetingTitle = displayName ? `${greeting}, ${displayName}` : `${greeting}!`;
  const companyId = useMemo(
    () => getCompanyId(empresa) || getCompanyId(user?.empresas?.[0]),
    [empresa, user]
  );
  const geralHook = useAutopilotInsightsGeral();
  const heroTitle = useMemo(() => {
    const { status } = getAutopilotHealthSummary(geralHook.data);
    return getHeroTitle(status);
  }, [geralHook.data]);
  const { weather, loading } = useDashboardWeather(companyId);
  const WeatherIcon = getWeatherIconByKey(weather?.iconKey);
  const condition = normalizeCondition(weather?.condition);
  const weatherLine = weather?.available
    ? [
        weather?.tempC !== null && weather?.tempC !== undefined
          ? `${weather.tempC}\u00b0C`
          : null,
        condition,
        weather?.city && weather?.state
          ? `${weather.city}/${weather.state}`
          : weather?.city || weather?.state || null,
      ]
        .filter(Boolean)
        .join(" \u2022 ") || "Clima indisponível no momento"
    : "Clima indisponível no momento";
  function openChat(initialMessage) {
    if (initialMessage) {
      history.push("/app/autopilot/chat", { initialMessage });
      return;
    }

    history.push("/app/autopilot/chat");
  }

  function goToMonitor() {
    if (monitorRef.current) {
      monitorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <div className="content">
      <Card className="card-everlog-shell card-everlog-hero" style={heroCardStyle}>
        <CardBody style={heroBodyStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <div style={heroLeadStyle}>
              <div style={heroIdentityRowStyle}>
                <div style={heroGreetingStyle}>
                  {greetingTitle}
                </div>
              </div>

              {!loading && weather?.available && (
                <div style={{ margin: "0.4rem 0 0.55rem", marginLeft: "-0.65rem" }}>
                  <div style={weatherBadgeStyle}>
                    <IconContext.Provider value={{ size: "14px", style: { flexShrink: 0 } }}>
                      <WeatherIcon />
                    </IconContext.Provider>
                    <span>{weatherLine}</span>
                  </div>
                </div>
              )}

              <CardTitle tag="h3" style={heroTitleStyle}>
                {heroTitle}
              </CardTitle>
            </div>

            <div
              style={{
                flex: "0 1 280px",
                minWidth: 236,
                maxWidth: 292,
                width: "100%",
              }}
            >
              <HealthGauge data={geralHook.data} loading={geralHook.loading} onClick={goToMonitor} />
            </div>
          </div>

          <div className="everlog-card-launcher" style={launcherCardStyle}>
            <button type="button" className="everlog-card-launcher-button" onClick={() => openChat()} style={launcherButtonStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
                <img
                  src={autopilotLogo}
                  alt="Autopilot"
                  style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", flexShrink: 0 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "var(--everlog-card-title)", fontSize: "0.88rem", fontWeight: 600 }}>
                    Clique aqui para falar com o Autopilot
                  </div>
                </div>
              </div>
              <span style={{ color: "var(--theme-accent)", fontSize: "1rem", flexShrink: 0 }}>↗</span>
            </button>

            <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.74rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "1rem", marginBottom: "0.1rem", paddingLeft: "0.9rem" }}>
              Comece por aqui
            </div>

            <div style={promptGridStyle}>
              {QUICK_PROMPTS.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => openChat(item.prompt)}
                  className="everlog-card-prompt-button"
                  // Em repouso, borda neutra (5 bordas coloridas juntas viram
                  // carnaval — feedback Rodrigo 12/06); a cor do ícone aparece
                  // só no hover, via var consumida no _global.scss.
                  style={{ ...promptButtonStyle, "--prompt-accent": item.color }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `color-mix(in srgb, ${item.color} 14%, transparent)`,
                      color: item.color,
                      flexShrink: 0,
                      fontSize: "0.8rem",
                    }}
                  >
                    <i className={`tim-icons ${item.icon}`} />
                  </span>
                  <span style={{ color: "var(--everlog-card-title)", fontSize: "0.84rem", fontWeight: 600 }}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <Row style={{ marginTop: "0.35rem", alignItems: "stretch" }}>
        <Col xl="8" lg="7" style={{ display: "flex" }}>
          <div ref={monitorRef} style={{ flex: 1, display: "flex" }}>
            <InsightsCard geralHook={geralHook} />
          </div>
        </Col>

        <Col xl="4" lg="5" style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          <FreightIndicatorsCard />
          <MarketIndicatorsCard />
          <EmissoesCarbonoCard />
        </Col>
      </Row>

      <Row style={{ marginTop: "0.35rem" }}>
        <Col md="12">
          <RadarLogistico />
        </Col>
      </Row>
    </div>
  );
};

export default Autopilot;
